import { Navigate, Route, Routes } from 'react-router';
import { AppShell } from './components/app-shell';
import { Toaster } from '@/components/ui/sonner';
import { useAuth } from './lib/auth';
import { OrgProvider } from './contexts/org-context';
import { AuditPage } from './routes/audit';
import { ConfigDiffPage } from './routes/config-diff';
import { ConfigEditorPage } from './routes/config-editor';
import { ConfigListPage } from './routes/configs';
import { DashboardPage } from './routes/dashboard';
import { FlagsPage } from './routes/flags';
import { AgentListPage } from './routes/agents';
import { AgentDetailPage } from './routes/agent-detail';
import { OperatorsRoute } from './routes/operators';
import { PiDevAgentDetailPage } from './routes/pi-dev-agent-detail';
import { InboxPage } from './routes/inbox';
import { SignInPage } from './routes/sign-in';
import { SignUpPage } from './routes/sign-up';
import { TeamsPage } from './routes/teams';
import { KeysPage } from './routes/keys';
import MembersPage from './routes/members';
import { UsageDashboardPage } from './routes/usage-dashboard';
import { InviteAcceptPage } from './routes/invite-accept';
import { TenantSettingsPage } from './routes/tenant-settings';
import { GitHubSettingsPage } from './routes/github-settings';
import { TimelinePage } from './routes/timeline';
import { TenantRunnerPoolPage } from './routes/tenant-runner-pool';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-700 border-t-emerald-500" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/sign-in" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        {/* Public invitation acceptance — not behind RequireAuth */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        {/* Legacy login route — redirect to /sign-in */}
        <Route path="/login" element={<Navigate to="/sign-in" replace />} />

        {/* Protected routes — require auth */}
        <Route
          element={
            <RequireAuth>
              <OrgProvider>
                <AppShell />
              </OrgProvider>
            </RequireAuth>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="configs" element={<ConfigListPage />} />
          <Route path="configs/:key" element={<ConfigEditorPage />} />
          <Route path="configs/:key/diff" element={<ConfigDiffPage />} />
          <Route path="flags" element={<FlagsPage />} />
          <Route path="agents" element={<AgentListPage />} />
          <Route path="agents/:id" element={<AgentDetailPage />} />
          <Route path="operators" element={<OperatorsRoute />} />
          <Route path="pi-agents/:id" element={<PiDevAgentDetailPage />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="teams" element={<TeamsPage />} />
          <Route path="keys" element={<KeysPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="usage" element={<UsageDashboardPage />} />
          <Route path="tenant-settings" element={<TenantSettingsPage />} />
          <Route path="github-settings" element={<GitHubSettingsPage />} />
          <Route path="timeline" element={<TimelinePage />} />
          <Route path="tenant-runner-pool" element={<TenantRunnerPoolPage />} />
        </Route>
      </Routes>
      <Toaster />
    </>
  );
}
